import React, { useCallback, useEffect, useRef, useState } from 'react';
import './MarketplacePracticeProfilesContainer.scss';
import { MarketplacePracticeProfile } from '../../components/MarketplaceSellerProfile/MarketplacePracticeProfile/MarketplacePracticeProfile';
import { PracticeProfileInfo } from '../../slices/profiles-data-slice/models/practice-profile-info';
import { useFormikContext } from 'formik';
import { NotificationFlag } from '../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../models/NotificationFlags/NotificationFlagsCategories';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../store/selectors';
import { addNotificationFlagToDelete } from '../../slices/notification-flags/notification-flags.slice';

interface MarketplacePracticeProfilesContainerProps {
    practices: PracticeProfileInfo[];
    profileId: string;
    setFormFieldValue(name: string, value: any): void;
    onAddLocationClicked(practiceIndex: number): void;
    removeLocation(practiceIndex: number, locationIndex: number): void;
    isEditable?: boolean;
    isAdmin?: boolean;
    isBroker?: boolean;
    dataSyncTrigger?: number;
    timelineId?: number;
    onSelectedPracticeChange?: (id: string) => void;
}

export const MarketplacePracticeProfilesContainer = (
    props: MarketplacePracticeProfilesContainerProps
) => {
    const { isEditable = true, timelineId } = props;
    const [selectedPracticeId, setSelectedPracticeId] = useState<string>(
        props.practices?.length ? props.practices[0]?.id : ''
    );

    const [practiceValidationErrors, setPracticeValidationErrors] = useState<
        Record<number, boolean>
    >({});

    const { errors } = useFormikContext();
    const dispatch = useDispatch() as any;
    const notificationFlags = useStateSelector(
        (s) => s.notificationFlags.flags
    );
    const selectedPracticeIdRef = useRef<string>(selectedPracticeId);
    const readPracticeNotificationsRef = useRef<(practiceId: string) => void>(() => {});

    const readPracticeNotifications = useCallback((practiceId: string) => {
        if (!timelineId || !practiceId) return;
        const prefix = `${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practiceId}`;
        notificationFlags
            .filter((f) => f.eventCode.toLowerCase().startsWith(prefix.toLowerCase()))
            .forEach((f) => dispatch(addNotificationFlagToDelete({ ...f, refreshAfterRemoval: true })));
    }, [timelineId, notificationFlags, dispatch]);

    useEffect(() => {
        selectedPracticeIdRef.current = selectedPracticeId;
    }, [selectedPracticeId]);

    readPracticeNotificationsRef.current = readPracticeNotifications;

    useEffect(() => {
        if (selectedPracticeId) {
            props.onSelectedPracticeChange?.(selectedPracticeId);
        }
    }, []);

    useEffect(() => {
        return () => {
            readPracticeNotificationsRef.current(selectedPracticeIdRef.current);
        };
    }, []);

    useEffect(() => {
        if (props.practices?.length && !!!selectedPracticeId) {
            setSelectedPracticeId(props.practices[0]?.id);
        }
    }, [props.practices]);

    useEffect(() => {
        if (!errors) {
            setPracticeValidationErrors({});
            return;
        }

        const practicesErrors = (errors as any).practiceProfiles as any[];
        const newErrorState: Record<number, boolean> = {};

        props.practices.forEach((_, index) => {
            newErrorState[index] = practiceHasErrors(index, practicesErrors);
        });

        setPracticeValidationErrors(newErrorState);
    }, [props.practices, errors]);

    const practiceHasErrors = (
        practiceIndex: number,
        practicesErrors: any[]
    ): boolean => {
        if (!practicesErrors) return false;

        if (Array.isArray(practicesErrors) && practicesErrors[practiceIndex]) {
            const practiceErrors = practicesErrors[practiceIndex];
            return practiceErrors && Object.keys(practiceErrors).length > 0;
        }

        return false;
    };

    const renderTabs = () => {
        return (
            <div className="body_inner_content marketplace-practice-profiles-tabs-container">
                <div className="RRT__container">
                    <div className="RRT__tabs">
                        {props.practices?.map((practice, index) => {
                            const hasErrors =
                                practiceValidationErrors[index] || false;
                            const baseClassName =
                                selectedPracticeId === practice.id
                                    ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                    : 'RRT__tab';
                            const className = hasErrors
                                ? `${baseClassName} mui-error`
                                : baseClassName;

                            return (
                                <div
                                    key={practice.id}
                                    className={className}
                                    onClick={() => {
                                        readPracticeNotifications(selectedPracticeId);
                                        setSelectedPracticeId(practice.id);
                                        props.onSelectedPracticeChange?.(practice.id);
                                    }}>
                                    {practice.practiceName}
                                    {timelineId && (
                                        <NotificationFlag
                                            flagClassName="notification-flag-wrapper"
                                            doNotRemove={true}
                                            includePrefix={true}
                                            eventCode={`${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practice.id}`}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                {selectedPracticeId && (
                    <MarketplacePracticeProfile
                        key={selectedPracticeId}
                        profileId={props.profileId}
                        practice={props.practices.find(
                            (f) => f.id === selectedPracticeId
                        )}
                        fieldPrefix={`practiceProfiles[${props.practices.findIndex(
                            (p) => p.id === selectedPracticeId
                        )}]`}
                        setFormFieldValue={props.setFormFieldValue}
                        onAddLocationClicked={() =>
                            props.onAddLocationClicked(
                                props.practices.findIndex(
                                    (p) => p.id === selectedPracticeId
                                )
                            )
                        }
                        removeLocation={(locationIndex: number) =>
                            props.removeLocation(
                                props.practices.findIndex(
                                    (p) => p.id === selectedPracticeId
                                ),
                                locationIndex
                            )
                        }
                        isEditable={isEditable}
                        isAdmin={props.isAdmin}
                        isBroker={props.isBroker}
                        dataSyncTrigger={props.dataSyncTrigger}
                        timelineId={timelineId}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="marketplace-practice-profiles-container">
            {renderTabs()}
        </div>
    );
};
